import React from 'react';
import useI18n from '@/hooks/useI18n';
import ToolTip from '../ToolTip';
import {
  formatCount,
  formatPercent,
  formatMoney,
  formatReturn,
} from '@/utils/numberUtils';
import type { ValueVsSpendMetrics } from '@/hooks/queries/useAnalyticsQueries';
import {
  SEGMENT_CHIP_COLORS,
  SEGMENT_COLORS,
  TEXT_PURPLE,
} from './analyticsTokens';
import SegmentedBar from './SegmentedBar';

const CGC_RATE_EUR = 150;
const DASH = '---';

const CHIP_TEXT_COLOR = TEXT_PURPLE;

const InfoIcon = ({ className = '' }: { className?: string }) => (
  <span
    className={`inline-block ${className}`}
    style={{ textTransform: 'none' }}
  >
    ⓘ
  </span>
);

const toNumber = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined) return 0;
  const n = typeof v === 'string' ? Number(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const segmentPercent = (amount: number, total: number): number => {
  if (total <= 0) return 0;
  return Math.max(0, Math.min(100, (amount / total) * 100));
};

interface BrandValueVsSpendComponentProps {
  metrics: ValueVsSpendMetrics | null | undefined;
}

const BrandValueVsSpendComponent = ({
  metrics,
}: BrandValueVsSpendComponentProps) => {
  const { t } = useI18n();

  const roi = metrics?.roi ?? 0;
  const totalCampaignValueNum = toNumber(metrics?.totalCampaignValue);
  const cgcNum = toNumber(metrics?.cgc);
  const emvNum = toNumber(metrics?.totalEmv);
  const salesNum = toNumber(metrics?.totalSales);
  const totalSpentNum = toNumber(metrics?.totalSpent);
  const approvedCount = metrics?.approvedCashbackCount ?? 0;

  const hasData =
    metrics != null &&
    (roi > 0 ||
      totalCampaignValueNum > 0 ||
      totalSpentNum > 0 ||
      approvedCount > 0);

  const cgcPct = segmentPercent(cgcNum, totalCampaignValueNum);
  const emvPct = segmentPercent(emvNum, totalCampaignValueNum);
  const salesPct = segmentPercent(salesNum, totalCampaignValueNum);

  return (
    <div>
      <h2
        className="mb-4"
        style={{
          color: '#121111',
          fontWeight: 500,
          fontSize: 14,
          lineHeight: '120%',
          letterSpacing: 0,
          opacity: 0.9,
        }}
      >
        {t('common.valueVsSpend')}
      </h2>
      <div className="flex flex-col md:flex-row gap-4">
        {/* Card 1 — ROI */}
        <div className="rounded-[24px] border border-[#E1D6FF] overflow-hidden flex flex-col md:w-[228px] md:flex-shrink-0">
          <div
            className="bg-white flex flex-col items-center justify-center text-center border-b border-[#E1D6FF]"
            style={{ padding: 24, gap: 16, minHeight: 186 }}
          >
            <div
              style={{
                color: '#2F1869',
                fontWeight: 600,
                fontSize: 48,
                lineHeight: '66px',
                letterSpacing: 0,
              }}
            >
              {hasData ? formatReturn(roi) : DASH}
            </div>
          </div>
          <div
            className="bg-white flex flex-col items-center justify-center text-center flex-1"
            style={{ padding: 24, gap: 4, minHeight: 122 }}
          >
            <div
              className="flex items-center justify-center gap-1 capitalize"
              style={{
                color: '#2B2A2A',
                fontWeight: 500,
                fontSize: 14,
                lineHeight: '16px',
              }}
            >
              {t('common.brandTotalReturn')}
              <ToolTip
                text={String(t('common.brandROITooltipDesc'))}
                position="top"
                className="tooltip-multiline"
              >
                <InfoIcon />
              </ToolTip>
            </div>
          </div>
        </div>

        {/* Card 2 — Total Value Created w/ stacked bar + per-segment mini cards */}
        <div className="bg-white border border-[#E1D6FF] rounded-[24px] flex flex-col md:flex-1 overflow-hidden">
          <div
            className="flex flex-col justify-center"
            style={{ padding: 24, gap: 24, minHeight: 186 }}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col" style={{ gap: 8 }}>
                <div
                  style={{
                    color: '#2F1869',
                    fontWeight: 600,
                    fontSize: 48,
                    lineHeight: '100%',
                    letterSpacing: 0,
                  }}
                >
                  {hasData ? formatMoney(totalCampaignValueNum) : DASH}
                </div>
                <div
                  className="capitalize"
                  style={{
                    color: '#2B2A2A',
                    fontWeight: 500,
                    fontSize: 14,
                    lineHeight: '16px',
                  }}
                >
                  {t('common.brandTotalValueCreated')}
                </div>
              </div>
              <div className="flex flex-col" style={{ gap: 4 }}>
                <LegendRow
                  color={SEGMENT_COLORS.cgc}
                  chipBackground={SEGMENT_CHIP_COLORS.cgc}
                  label="CGC"
                  percent={cgcPct}
                  hasData={hasData}
                />
                <LegendRow
                  color={SEGMENT_COLORS.emv}
                  chipBackground={SEGMENT_CHIP_COLORS.emv}
                  label={String(t('common.earnedMediaValueFull'))}
                  percent={emvPct}
                  hasData={hasData}
                />
                <LegendRow
                  color={SEGMENT_COLORS.sales}
                  chipBackground={SEGMENT_CHIP_COLORS.sales}
                  label={String(t('common.salesGenerated'))}
                  percent={salesPct}
                  hasData={hasData}
                />
              </div>
            </div>

            <SegmentedBar
              height={12}
              gap={4}
              ariaLabel={`CGC ${formatPercent(cgcPct)}, EMV ${formatPercent(emvPct)}, Sales ${formatPercent(salesPct)}`}
              segments={
                !hasData
                  ? [
                      { color: SEGMENT_COLORS.cgc, percent: 33.33 },
                      { color: SEGMENT_COLORS.emv, percent: 33.33 },
                      { color: SEGMENT_COLORS.sales, percent: 33.34 },
                    ]
                  : [
                      { color: SEGMENT_COLORS.cgc, percent: cgcPct },
                      { color: SEGMENT_COLORS.emv, percent: emvPct },
                      { color: SEGMENT_COLORS.sales, percent: salesPct },
                    ]
              }
            />
          </div>

          {/* Bottom band: 3 mini-cards */}
          <div
            className="border-t border-[#E1D6FF] grid grid-cols-3 divide-x divide-[#E1D6FF]"
            style={{ padding: 24, minHeight: 122 }}
          >
            <SegmentMiniCard
              label="CGC"
              amount={cgcNum}
              hasData={hasData}
              tooltip={String(
                t('common.CGCTooltipDesc', { rate: CGC_RATE_EUR }),
              )}
            />
            <SegmentMiniCard
              label={String(t('common.earnedMediaValueFull'))}
              amount={emvNum}
              hasData={hasData}
              tooltip={String(t('common.EMVTooltipDesc'))}
            />
            <SegmentMiniCard
              label={String(t('common.salesGenerated'))}
              amount={salesNum}
              hasData={hasData}
            />
          </div>
        </div>

        {/* Card 3 — Total Campaign Spend */}
        <div className="bg-white border border-[#E1D6FF] rounded-[24px] flex flex-col overflow-hidden md:w-[228px] md:flex-shrink-0">
          <div
            className="border-b border-[#E1D6FF] flex flex-col items-center justify-center text-center"
            style={{ padding: 24, gap: 20, minHeight: 186 }}
          >
            <div
              style={{
                color: '#2F1869',
                fontWeight: 600,
                fontSize: 48,
                lineHeight: '66px',
                letterSpacing: 0,
              }}
            >
              {hasData ? formatMoney(totalSpentNum) : DASH}
            </div>
            <div
              className="capitalize"
              style={{
                color: '#2B2A2A',
                fontWeight: 500,
                fontSize: 14,
                lineHeight: '16px',
              }}
            >
              {t('common.brandTotalCampaignSpend')}
            </div>
          </div>
          <div
            className="flex flex-col items-center justify-center text-center flex-1"
            style={{ padding: 24, gap: 4, minHeight: 122 }}
          >
            <div
              style={{
                color: '#191B1C',
                fontWeight: 400,
                fontSize: 24,
                lineHeight: '32px',
                letterSpacing: 0,
              }}
            >
              {hasData ? formatCount(approvedCount) : DASH}
            </div>
            <div
              style={{
                color: '#2B2A2A',
                fontWeight: 400,
                fontSize: 14,
                lineHeight: '19px',
              }}
            >
              {t('common.creatorsRewardedCaption')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface LegendRowProps {
  color: string;
  chipBackground: string;
  label: string;
  percent: number;
  hasData: boolean;
}

const LegendRow = ({
  color,
  chipBackground,
  label,
  percent,
  hasData,
}: LegendRowProps) => (
  <div className="flex items-center" style={{ gap: 16, height: 20 }}>
    <span
      className="inline-block rounded-full flex-shrink-0"
      style={{ width: 16, height: 16, backgroundColor: color }}
    />
    <span
      className="capitalize"
      style={{
        color: '#2B2A2A',
        fontWeight: 400,
        fontSize: 12,
        lineHeight: '16px',
      }}
    >
      {label}
    </span>
    <span
      className="capitalize rounded-full inline-flex items-center justify-center"
      style={{
        backgroundColor: chipBackground,
        color: CHIP_TEXT_COLOR,
        fontWeight: 600,
        fontSize: 12,
        lineHeight: '16px',
        padding: '2px 8px',
      }}
    >
      {hasData ? formatPercent(percent) : DASH}
    </span>
  </div>
);

interface SegmentMiniCardProps {
  label: string;
  amount: number;
  hasData: boolean;
  tooltip?: string;
}

const SegmentMiniCard = ({
  label,
  amount,
  hasData,
  tooltip,
}: SegmentMiniCardProps) => (
  <div
    className="flex flex-col items-center justify-center text-center"
    style={{ padding: '0 8px', gap: 4 }}
  >
    <div
      style={{
        color: '#191B1C',
        fontWeight: 400,
        fontSize: 24,
        lineHeight: '32px',
        letterSpacing: 0,
      }}
    >
      {hasData ? formatMoney(amount) : DASH}
    </div>
    <div
      className="capitalize flex items-center justify-center"
      style={{
        color: '#9B919D',
        fontWeight: 400,
        fontSize: 12,
        lineHeight: '16px',
        gap: 4,
      }}
    >
      {label}
      {tooltip ? (
        <ToolTip text={tooltip} position="top" className="tooltip-multiline">
          <InfoIcon />
        </ToolTip>
      ) : null}
    </div>
  </div>
);

export default BrandValueVsSpendComponent;
